import React from 'react'
import PropTypes from 'prop-types'
import {
    PasswordAmountRange,
    PasswordLengthRange,
    AllowUppercaseLettersCheckbox,
    AllowLowercaseLettersCheckbox,
    AllowNumbersCheckbox,
    AllowSpecialCharactersCheckbox,
    AllowUmlautsCheckbox,
    AllowZerosCheckbox,
    EasyToRememberCheckbox,
    Text,
    Button,
    GeneratorHeader,
    GeneratorFooter,
} from '../../Presentational'
import styled, {keyframes} from 'styled-components'
import {getPasswordStrengthParameters, i18n} from '../../../Utils'
import {ExpandableSection, PasswordsResultContainer} from '../'
import isColor from 'is-color'
import NotificationContainer from '../Molecules/NotificationContainer'

const uppercaseLettersPattern = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
const lowercaseLettersPattern = 'abcdefghjkmnpqrstuvwxyz'
const lowercaseUmlautsPattern = 'öäü'
const uppercaseUmlautsPattern = 'ÖÄÜ'
const zerosPattern = '0'
const numbersPattern = '23456789'
const specialCharactersPattern = ',.:-_#+~<>!§$%&(){}=?@'
const defaultNumberOfPasswords = 1
const defaultColorObject = {
    titleColor: '#36a1c9',
    optionsColor: '#0b5f99',
    headlineColor: '#0b5f99',
    buttonBackgroundColor: '#36a1c9',
    buttonTextColor: '#ffffff',
    backgroundColor: '#ffffff',
    borderColor: '#d9e2e9',
    passwordResultColor: '',
}

class PasswordGenerator extends React.Component {
    constructor (props) {
        super(props)
        this.state = {
            passwords: [],
            allowLowercaseLetters: true,
            allowUppercaseLetters: true,
            allowNumbers: true,
            allowSpecialCharacters: true,
            allowedSpecialCharacters: specialCharactersPattern,
            easyToRemember: false,
            allowUmlauts: false,
            allowZeros: false,
            invalidSepcialCharacter: false,
            numberOfPasswords: defaultNumberOfPasswords,
            passwordLength: 16,
            lastToast: -1,
            ...defaultColorObject,
        }
    }

    componentDidMount() {
        const colors = this.cleanUpWrongColorCodes(this.props)
        this.setState(colors)
    }

    componentWillReceiveProps(newProps) {
        const colors = this.cleanUpWrongColorCodes(newProps)
        this.setState(colors)
    }

    cleanUpWrongColorCodes = colorObject => {
        let returnObject = JSON.parse(JSON.stringify(colorObject))

        Object.keys(returnObject).forEach(key => {
            if (!isColor(returnObject[key]) && defaultColorObject.hasOwnProperty(key)) {
                returnObject[key] = defaultColorObject[key]
            }
        })

        return returnObject
    }

    generateHardPassword () {
        const {
            allowLowercaseLetters,
            allowUppercaseLetters,
            allowNumbers,
            allowSpecialCharacters,
            allowedSpecialCharacters,
            passwordLength,
            allowUmlauts,
            allowZeros,
        } = this.state

        let password = ''

        let pattern = ''
        if (allowLowercaseLetters === true) {
            pattern = pattern.concat(lowercaseLettersPattern)
        }
        if (allowUppercaseLetters === true) {
            pattern = pattern.concat(uppercaseLettersPattern)
        }
        if (allowNumbers === true) {
            pattern = pattern.concat(numbersPattern)
        }
        if (allowSpecialCharacters === true) {
            pattern = pattern.concat(allowedSpecialCharacters)
        }
        if (allowUmlauts === true && allowLowercaseLetters === true) {
            pattern = pattern.concat(lowercaseUmlautsPattern)
        }
        if (allowUmlauts === true && allowUppercaseLetters === true) {
            pattern = pattern.concat(uppercaseUmlautsPattern)
        }
        if (allowUmlauts === true && allowUppercaseLetters === false && allowLowercaseLetters === false) {
            pattern = pattern.concat(uppercaseUmlautsPattern + lowercaseUmlautsPattern)
        }
        if (allowZeros === true) {
            pattern = pattern.concat(zerosPattern)
        }

        for (let i = 0; i < Number(passwordLength); i++) {
            let randomNumber = Math.floor(Math.random() * pattern.length - 1) + 1
            password += pattern.charAt(randomNumber)
        }

        return password
    }

    generateEasyPassword() {
        const {
            passwordLength,
        } = this.state

        let password = ''

        const helperObject = {
            consonant: ['b','c','d','f','g','h','j','k','l','m','n','p','r','s','t','v','w','x','y','z'],
            vocal: ['a','e','i','o','u'],
        }

        let current = {
            name: 'consonant',
            max: helperObject.consonant.length - 1
        }
        this.generatePasswords = this.generatePasswords.bind(this)

        for (let i = 0; i < Number(passwordLength); i++) {
            let randomNumber = Math.floor(Math.random() * current.max) + 1
            password += helperObject[current.name][randomNumber]

            if (current.name === 'consonant') {
                current.name = 'vocal'
                current.max = helperObject.vocal.length - 1
            } else {
                current.name = 'consonant'
                current.max = helperObject.consonant.length - 1
            }
        }

        return password
    }


    generatePasswords (event) {
        event.preventDefault()
        const {
            numberOfPasswords,
            easyToRemember,
            passwordLength,
        } = this.state

        let passwords = []

        for (let i = 0; i < Number(numberOfPasswords); i++) {
            if (easyToRemember === true) {
                passwords.push(this.generateEasyPassword())
            } else {
                passwords.push(this.generateHardPassword())
            }
        }

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'GAEvent',
            'eventCategory': 'settings',
            'eventAction': 'passwordLength',
            'eventLabel': passwordLength,
            'eventValue': '1',
        });
        window.dataLayer.push({
            'event': 'GAEvent',
            'eventCategory': 'settings',
            'eventAction': 'passwordCount',
            'eventLabel': numberOfPasswords,
            'eventValue': '1',
        });

        this.setState({
            passwords: passwords,
            passwordResultColor: getPasswordStrengthParameters(this.state).color,
        })
    }

    checkIfAtLeastOneOptionIsSelected (lastUnchecked) {
        const {
            allowSpecialCharacters,
            allowLowercaseLetters,
            allowUppercaseLetters,
            allowNumbers,
            allowZeros,
            allowUmlauts,
            easyToRemember,
        } = this.state
        let counter = 0

        if (allowSpecialCharacters === false) counter++
        if (allowLowercaseLetters === false) counter++
        if (allowUppercaseLetters === false) counter++
        if (allowNumbers === false) counter++
        if (allowZeros === false) counter++
        if (allowUmlauts === false) counter++
        if (easyToRemember === false) counter++

        if (counter === 7) {
            let stateObject = {}

            this.addAlert('top', i18n('notification.text.options_checked'))

            stateObject[lastUnchecked] = true
            this.setState(stateObject)
        }
    }

    addAlert = (position, message, duration) => {
        this.notifications.addAlert(position, message, duration)
    }

    onChange (name, value) {
        let stateObject = {}

        if (name === 'easyToRemember' && value === true) {
            stateObject['allowLowercaseLetters'] = false
            stateObject['allowUppercaseLetters'] = false
            stateObject['allowNumbers'] = false
            stateObject['allowZeros'] = false
            stateObject['allowUmlauts'] = false
            stateObject['allowSpecialCharacters'] = false
            stateObject['passwordLength'] = 8
        } else if (name !== 'easyToRemember' && value === true) {
            stateObject['easyToRemember'] = false
        }

        if (name === 'allowedSpecialCharacters' && !(new RegExp('^[,.:\\-_#+~<>!§$%&(){}=?@]+$').test(value))) {
            this.setState({invalidSepcialCharacter: true}, () => setTimeout(() => this.setState({invalidSepcialCharacter: false}), 500))
            return
        }

        stateObject[name] = value
        this.setState(stateObject, () => this.checkIfAtLeastOneOptionIsSelected(name))
    }

    render () {
        const {
            passwords,
            allowLowercaseLetters,
            allowUppercaseLetters,
            allowNumbers,
            allowSpecialCharacters,
            allowedSpecialCharacters,
            numberOfPasswords,
            passwordLength,
            easyToRemember,
            allowUmlauts,
            allowZeros,
            invalidSepcialCharacter,
            headlineColor,
            optionsColor,
            backgroundColor,
            buttonBackgroundColor,
            buttonTextColor,
            titleColor,
            borderColor,
            passwordResultColor,
        } = this.state

        const {
            showExpertSettings = true,
            embedded = false,
            borderWidth,
        } = this.props

        const passwordStrengthParameters = getPasswordStrengthParameters(this.state)

        return (
            <NotificationContainer ref={ref => this.notifications = ref}>
            <Wrapper backgroundColor={backgroundColor}>
                <Border backgroundColor={backgroundColor} borderColor={borderColor} borderWidth={borderWidth}>
                    <GeneratorHeader labelColor={headlineColor}/>
                    <form
                        id={'password-generator-form'}
                        onSubmit={this.generatePasswords.bind(this)}>
                        <PasswordLengthRange
                            onChange={(event) => this.onChange('passwordLength', event.value)}
                            value={passwordLength}
                            passwordStrengthString={passwordStrengthParameters.time}
                            labelColor={titleColor}
                            rangeColor={passwordStrengthParameters.color}
                            level={passwordStrengthParameters.level}
                            icon={passwordStrengthParameters.icon}
                            onInfoClick={() => this.addAlert('top', i18n('form.tooltip.security_hint'), 5000)}/>
                        {/* This can be a string provided by query params */}
                        {(showExpertSettings === true || showExpertSettings === 'true') &&
                        <ExpandableSection
                            toggleLinkLabel={'expandable.label.expert_settings'}
                            labelColor={titleColor}
                            onInfoClick={() => this.addAlert('top', i18n('settings.tooltip.settings_hint'), 5000)}>
                            <PasswordAmountRange
                                onChange={(event) => this.onChange('numberOfPasswords', event.value)}
                                value={numberOfPasswords}
                                labelColor={optionsColor}/>
                            <SettingsWrapper>
                                <AllowUppercaseLettersCheckbox
                                    checked={allowUppercaseLetters}
                                    onChange={(event) => this.onChange('allowUppercaseLetters', event.target.checked)}
                                    labelColor={optionsColor}/>
                                <AllowLowercaseLettersCheckbox
                                    checked={allowLowercaseLetters}
                                    onChange={(event) => this.onChange('allowLowercaseLetters', event.target.checked)}
                                    labelColor={optionsColor}/>
                                <AllowNumbersCheckbox
                                    checked={allowNumbers}
                                    onChange={(event) => this.onChange('allowNumbers', event.target.checked)}
                                    labelColor={optionsColor}/>
                                <EasyToRememberCheckbox
                                    checked={easyToRemember}
                                    onChange={(event) => this.onChange('easyToRemember', event.target.checked)}
                                    labelColor={optionsColor}/>
                                <Order index={1}>
                                    <AllowSpecialCharactersCheckbox
                                        checked={allowSpecialCharacters}
                                        onChange={(event) => this.onChange('allowSpecialCharacters', event.target.checked)}
                                        labelColor={optionsColor}/>
                                </Order>
                                <AllowUmlautsCheckbox
                                    checked={allowUmlauts}
                                    onChange={(event) => this.onChange('allowUmlauts', event.target.checked)}
                                    labelColor={optionsColor}/>
                                <Order index={2}>
                                    <AllowedCharactersWrapper invalid={invalidSepcialCharacter}>
                                        <Text
                                            label={'form.checkbox.label.allowed_special_characters'}
                                            id={'allowed-special-characters-input'}
                                            inputStyle={'small'}
                                            borderColor={'#d9e2e9'}
                                            value={allowedSpecialCharacters}
                                            disabled={!allowSpecialCharacters}
                                            onChange={(event) => this.onChange('allowedSpecialCharacters', event.target.value)}
                                            autoComplete={'off'}/>
                                    </AllowedCharactersWrapper>
                                </Order>
                                <AllowZerosCheckbox
                                    checked={allowZeros}
                                    onChange={(event) => this.onChange('allowZeros', event.target.checked)}
                                    labelColor={optionsColor}/>
                            </SettingsWrapper>
                        </ExpandableSection>}
                        <ButtonWrapper>
                            <Button
                                id={'generate-password-button'}
                                label={numberOfPasswords > 1
                                    ? i18n('form.button.label.generate_password',
                                        {passwordString: i18n('form.range.label.number_of_passwords', {number: ''})},
                                    )
                                    : i18n('form.button.label.generate_password',
                                        {passwordString: i18n('form.range.label.number_of_passwords_singular', {number: ''})},
                                    )
                                }
                                backgroundColor={buttonBackgroundColor}
                                textColor={buttonTextColor}
                                width={numberOfPasswords > 1 ? 260 : 240}/>
                        </ButtonWrapper>
                    </form>
                    <PasswordsResultContainer
                        passwords={passwords}
                        refreshAction={this.generatePasswords.bind(this)}
                        backgroundColor={passwordResultColor}
                        onCopy={(text, result) => result && this.addAlert('left', i18n('alert.copied_password'), 1500)}/>
                    <GeneratorFooter labelColor={titleColor} showSubline={embedded === true || embedded === 'true'}/>
                </Border>
            </Wrapper>
            </NotificationContainer>
        )
    }
}

PasswordGenerator.propTypes = {
    backgroundColor: PropTypes.string,
    titleColor: PropTypes.string,
    headlineColor: PropTypes.string,
    optionsColor: PropTypes.string,
    buttonBackgroundColor: PropTypes.string,
    buttonTextColor: PropTypes.string,
    // comes as string if given by query params, thats why string is allowed and we cast it to bool
    showExpertSettings: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    // comes as string if given by query params, thats why string is allowed and we cast it to bool
    embedded: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    borderWidth: PropTypes.string,
}

export default PasswordGenerator


const invalidKeyFrames = keyframes`
    8%, 41% {
        -webkit-transform: translateX(-10px);
    }
    25%, 58% {
        -webkit-transform: translateX(10px);
    }
    75% {
        -webkit-transform: translateX(-5px);
    }
    92% {
        -webkit-transform: translateX(5px);
    }
    0%, 100% {
        -webkit-transform: translateX(0);
    }
`

const Wrapper = styled.div`
    background-color: transparent;
    width: 100%;
    box-sizing: border-box;
    position: relative;
`

const Border = styled.div`
    position: relative;
    border-radius: 5px;
    border: ${props => props.borderWidth ? props.borderWidth : '1px'} solid ${props => props.borderColor ? props.borderColor : '#d9e2e9'};
    background-color: ${props => props.backgroundColor ? props.backgroundColor : 'white'};
    padding: 20px;
    padding-top: 0;
`

const SettingsWrapper = styled.div`
    padding: 0 10px;
    display: flex;
    flex-direction: column;
    @media (min-width: 768px) {
        display: block;
        & > div {
            display: inline-block;
            width: 50%;
        }
    }
`

const AllowedCharactersWrapper = styled.div`
    padding: 5px 0px 5px 29px;
    animation-duration: .5s;
    animation-name: ${props => props.invalid && invalidKeyFrames};

`

const Order = styled.div`
    order: ${props => props.index ? props.index : 0};
`

const ButtonWrapper = styled.div`
    padding: 20px 0;
    text-align: center;
`



// WEBPACK FOOTER //
// ./src/jsx/Components/Container/Organisms/PasswordGenerator.jsx